// Document overview tab: a plain summary (title, version, validity, publisher, pages, language, licence) and the sections list with code and title; for Operator and Admin one collapsible Build details section holds the KPI tiles, the sections table with priority, counts and clause coverage, requirements by parameter group and the modality and value-type breakdowns.
import { Link } from "@tanstack/react-router";
import {
  BookAIcon,
  ChartLineIcon,
  ChevronDownIcon,
  ClipboardListIcon,
  ImageIcon,
  ListTreeIcon,
  Rows3Icon,
  SlidersHorizontalIcon,
  Table2Icon,
} from "lucide-react";

import type { DocumentOut, DocumentStats, SectionStats } from "@/api/__generated__";
import { useInternalDetails } from "@/app/useInternalDetails";
import { KeyValueTable } from "@/components/common/KeyValueTable";
import { KpiCard } from "@/components/common/KpiCard";
import { Legend } from "@/components/common/Legend";
import { Mono } from "@/components/common/Mono";
import { PriorityBadge } from "@/components/common/PriorityBadge";
import { ProblemState } from "@/components/common/ProblemState";
import { SectionTitle } from "@/components/common/SectionTitle";
import { StackBar } from "@/components/common/StackBar";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarList } from "@/features/library/BarList";
import {
  coverageFromStatuses,
  coverageLegend,
  coverageSegments,
  coverageSummary,
} from "@/features/library/coverage";
import { languageName, LICENCE_LABELS } from "@/features/library/format";
import { validityText } from "@/features/versions/VersionTimeline";
import { linkClass } from "@/lib/cn";
import { formatNumber, plural } from "@/lib/format";
import { MODALITY_LABELS, VALUE_TYPE_LABELS, labelOf } from "@/lib/labels";

interface DocumentOverviewTabProps {
  docUid: string;
  region: string;
  document: DocumentOut | undefined;
  publisher: string | null;
  stats: DocumentStats | undefined;
  isPending: boolean;
  error: unknown;
  onRetry: () => void;
}

function sectionCoverage(section: SectionStats) {
  return coverageFromStatuses({
    extracted: section.extracted,
    reused: section.reused,
    informative: section.informative,
    pending: section.pending,
    failed: section.failed,
    not_in_scope: section.not_in_scope,
  });
}

function sortedEntries(counts: Readonly<Record<string, number>>): [string, number][] {
  return Object.entries(counts).toSorted((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

function OverviewSkeleton() {
  return (
    <div aria-hidden className="grid grid-cols-1 items-start gap-3.5 xl:grid-cols-[420px_minmax(0,1fr)]">
      <Skeleton className="h-72 w-full rounded-lg" />
      <Skeleton className="h-80 w-full rounded-lg" />
    </div>
  );
}

function SummaryCard({ document, publisher }: { document: DocumentOut; publisher: string | null }) {
  return (
    <Card role="region" aria-labelledby="document-summary" className="px-5 py-4.5">
      <SectionTitle id="document-summary" as="h2" title="Summary" />
      <KeyValueTable
        caption="Document summary"
        className="mt-2 [&_td]:font-normal"
        rows={[
          { key: "title", label: "Title", value: document.title },
          { key: "version", label: "Version", value: <Mono tone="ink">{document.version_label}</Mono> },
          { key: "validity", label: "Validity", value: validityText(document) },
          { key: "publisher", label: "Publisher", value: publisher ?? "Not recorded" },
          {
            key: "pages",
            label: "Pages",
            value: document.page_count === null ? "Not recorded" : formatNumber(document.page_count),
          },
          { key: "language", label: "Language", value: languageName(document.language) },
          { key: "licence", label: "Licence", value: LICENCE_LABELS[document.licence_class] },
        ]}
      />
    </Card>
  );
}

function SectionList({ stats, docUid, region }: { stats: DocumentStats; docUid: string; region: string }) {
  return (
    <Card role="region" aria-labelledby="document-section-list" className="overflow-hidden">
      <div className="px-5 pt-4.5 pb-2">
        <SectionTitle id="document-section-list" as="h2" title="Sections" />
      </div>
      {stats.sections.length === 0 ? (
        <p className="m-0 px-5 pb-4.5 text-ui text-muted-foreground">
          No sections are recorded for this version.
        </p>
      ) : (
        <ul aria-labelledby="document-section-list" className="m-0 list-none p-0 pb-2">
          {stats.sections.map((section) => (
            <li
              key={section.section_code}
              className="grid grid-cols-[96px_minmax(0,1fr)] items-baseline gap-3 border-t border-border-subtle px-5 py-2"
            >
              <Link
                to="/$region/library/$docUid"
                params={{ region, docUid }}
                search={{ tab: "contents", clause: `${docUid}#${section.section_code}` }}
                className={linkClass}
              >
                <Mono tone="blue">{section.section_code}</Mono>
              </Link>
              <span className="min-w-0 text-ui text-foreground">{section.title}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function SectionsCard({ stats, docUid, region }: { stats: DocumentStats; docUid: string; region: string }) {
  const total = coverageFromStatuses(stats.clauses_by_extraction_status);
  return (
    <Card role="region" aria-labelledby="document-sections" className="overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-4.5 pb-3">
        <SectionTitle
          id="document-sections"
          as="h2"
          title="Clause coverage by section"
          sub={`${plural(stats.sections.length, "section")} · ${coverageSummary(total)}`}
        />
        <Legend items={coverageLegend(total)} />
      </div>
      <Table aria-labelledby="document-sections">
        <TableHeader>
          <TableRow>
            <TableHead>Code</TableHead>
            <TableHead>Title</TableHead>
            <TableHead>Priority</TableHead>
            <TableHead className="text-right">Clauses</TableHead>
            <TableHead className="text-right">Requirements</TableHead>
            <TableHead className="w-[28%]">Clause coverage</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {stats.sections.map((section) => {
            const coverage = sectionCoverage(section);
            return (
              <TableRow key={section.section_code}>
                <TableCell>
                  <Link
                    to="/$region/library/$docUid"
                    params={{ region, docUid }}
                    search={{ tab: "contents", clause: `${docUid}#${section.section_code}` }}
                    className={linkClass}
                  >
                    <Mono tone="blue">{section.section_code}</Mono>
                  </Link>
                </TableCell>
                <TableCell className="max-w-[320px] truncate">{section.title}</TableCell>
                <TableCell>
                  <PriorityBadge priority={section.priority} />
                </TableCell>
                <TableCell className="text-right">{formatNumber(section.clauses)}</TableCell>
                <TableCell className="text-right">
                  {section.requirements > 0 ? (
                    <Link
                      to="/$region/workspace"
                      params={{ region }}
                      search={{ doc: docUid, section: section.section_code }}
                      className={`${linkClass} font-semibold`}
                    >
                      {formatNumber(section.requirements)}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">0</span>
                  )}
                </TableCell>
                <TableCell>
                  <StackBar
                    label={`${section.section_code} clause coverage`}
                    segments={coverageSegments(coverage)}
                    total={coverage.total}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

export function DocumentOverviewTab({
  docUid,
  region,
  document,
  publisher,
  stats,
  isPending,
  error,
  onRetry,
}: DocumentOverviewTabProps) {
  const internal = useInternalDetails();
  if (error !== null && error !== undefined) {
    return (
      <Card>
        <ProblemState problem={error} retry={onRetry} />
      </Card>
    );
  }
  if (isPending || stats === undefined || document === undefined) {
    return <OverviewSkeleton />;
  }
  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid grid-cols-1 items-start gap-3.5 xl:grid-cols-[420px_minmax(0,1fr)]">
        <SummaryCard document={document} publisher={publisher} />
        <SectionList stats={stats} docUid={docUid} region={region} />
      </div>
      {internal ? <BuildDetails stats={stats} docUid={docUid} region={region} /> : null}
    </div>
  );
}

function BuildDetails({ stats, docUid, region }: { stats: DocumentStats; docUid: string; region: string }) {
  const { counts, quality } = stats;
  const groups = stats.requirements_by_group
    .filter((group) => group.count > 0)
    .toSorted((a, b) => b.count - a.count || a.order - b.order);
  return (
    <Collapsible className="rounded-lg border bg-card shadow-1">
      <CollapsibleTrigger className="group flex w-full items-center justify-between gap-3 rounded-lg border-0 bg-transparent px-5 py-3.5 text-left hover:bg-accent">
        <span className="flex flex-col">
          <span className="text-ui font-semibold text-foreground">Build details</span>
          <span className="text-xs text-muted-foreground">
            Extraction counts, clause coverage and quality, for operators.
          </span>
        </span>
        <ChevronDownIcon
          aria-hidden
          className="size-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180"
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-3.5 border-t px-3.5 py-3.5">
        <section aria-label="Document figures" className="grid grid-cols-2 gap-3.5 xl:grid-cols-4">
          <KpiCard
            icon={ListTreeIcon}
            label="Clauses"
            value={formatNumber(counts.clauses)}
            sub={plural(stats.sections.length, "section")}
          />
          <KpiCard
            icon={ClipboardListIcon}
            label="Requirements"
            value={formatNumber(counts.requirements)}
            sub={`${formatNumber(quality.requirements_flagged)} flagged · ${formatNumber(quality.low_confidence)} low confidence`}
          />
          <KpiCard
            icon={ChartLineIcon}
            label="Curves"
            value={formatNumber(counts.curves)}
            sub={plural(counts.curve_families, "curve family", "curve families")}
          />
          <KpiCard
            icon={Rows3Icon}
            label="Bands"
            value={formatNumber(counts.bands)}
            sub={`${formatNumber(counts.open_parameters)} open parameters`}
          />
          <KpiCard
            icon={BookAIcon}
            label="Definitions"
            value={formatNumber(counts.definitions)}
            sub={`${formatNumber(stats.definitions_used)} used in clauses`}
          />
          <KpiCard
            icon={ImageIcon}
            label="Figures"
            value={formatNumber(counts.figures)}
            sub={`${formatNumber(counts.standards)} standards cited`}
          />
          <KpiCard
            icon={Table2Icon}
            label="Tables"
            value={formatNumber(counts.tables)}
            sub={`${formatNumber(stats.references.to_clauses)} cross-references`}
          />
          <KpiCard
            icon={SlidersHorizontalIcon}
            label="Parameters used"
            value={formatNumber(counts.parameters_used)}
            sub={plural(counts.thresholds, "plant-type threshold")}
          />
        </section>
        <div className="grid grid-cols-1 items-start gap-3.5 xl:grid-cols-[minmax(0,1fr)_380px]">
          <SectionsCard stats={stats} docUid={docUid} region={region} />
          <div className="flex flex-col gap-3.5">
            <Card role="region" aria-labelledby="document-groups" className="px-5 py-4.5">
              <SectionTitle id="document-groups" as="h2" title="Requirements by parameter group" />
              {groups.length === 0 ? (
                <p className="m-0 mt-3 text-ui text-muted-foreground">
                  No requirement is mapped to a parameter yet.
                </p>
              ) : (
                <BarList
                  className="mt-4"
                  ariaLabel="Requirements by parameter group"
                  items={groups.map((group) => ({
                    key: group.group_id,
                    label: group.name,
                    value: group.count,
                  }))}
                />
              )}
            </Card>
            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
              <Card role="region" aria-labelledby="document-modality" className="px-5 py-4.5">
                <SectionTitle id="document-modality" as="h2" title="Modality" />
                <BarList
                  className="mt-4"
                  tone="ramp"
                  ariaLabel="Requirements by modality"
                  items={sortedEntries(stats.requirements_by_modality).map(([key, value]) => ({
                    key,
                    label: labelOf(MODALITY_LABELS, key as keyof typeof MODALITY_LABELS),
                    value,
                  }))}
                />
              </Card>
              <Card role="region" aria-labelledby="document-value-types" className="px-5 py-4.5">
                <SectionTitle id="document-value-types" as="h2" title="Value type" />
                <BarList
                  className="mt-4"
                  tone="ramp"
                  ariaLabel="Requirements by value type"
                  items={sortedEntries(stats.requirements_by_value_type).map(([key, value]) => ({
                    key,
                    label: labelOf(VALUE_TYPE_LABELS, key as keyof typeof VALUE_TYPE_LABELS),
                    value,
                  }))}
                />
              </Card>
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
